// Resource Center Page
// Complementary tools and services that work alongside AI tools
// Design: Same warm minimalism as main page

import { motion } from "framer-motion";
import { BUTTON } from "@/lib/buttonStyles";
import { Link } from "wouter";
import { resources as allResources, type PricingType } from "@/lib/resourcesData";
import { isPublished } from "@/lib/toolsData";

import BrandMark, { hasBrandMark } from "@/components/BrandMark";
import PricingBadge from "@/components/PricingBadge";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
const resources = allResources.filter(isPublished);

const HERO_BG = "/assets/hero-bg-Y8F6k5ayA9btVQcqwjUfpV.webp";

export default function ResourceCenter() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader context="The Resource Center" backHref="/" backLabel="Back to AI Tools" />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-background/55" />
        {/* Condensed from py-16/md:py-24. The eyebrow went with it: it read
            "THE RESOURCE CENTER" directly above a headline whose first line
            was "The Resource Center", and the header's own context slot says
            it a third time. */}
        <div className="relative container py-10 md:py-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            /* max-w-3xl, not 2xl. At text-5xl the second line wrapped, so the
               headline ran to three lines and left "Deeper" stranded on its
               own. This is the width that keeps it to the two it is written
               as — check it again if the wording changes. */
            className="max-w-3xl"
          >
            <h1 className="font-['Fraunces'] text-4xl md:text-5xl font-bold text-foreground leading-tight mb-3">
              The Resource Center<br />
              <em className="not-italic text-[oklch(0.32_0.08_255)]">Learn, Practice, and Go Deeper</em>
            </h1>
            <p className="text-base md:text-lg text-foreground/75 leading-relaxed max-w-xl">
              Beyond AI tools, these resources help you host, manage, and amplify the content you create. Build your complete creator toolkit.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── RESOURCES GRID ── */}
      <section className="bg-background">
        <div className="container py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource, i) => (
              <motion.div
                key={resource.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Header: mark + name/category + pricing badge */}
                <div className="flex items-start justify-between mb-3">
                  {/* items-start, not center. The name and its category are
                      two lines here (three when the category wraps), so
                      centring floated the mark down beside the category rather
                      than beside the name it belongs to — and it sat at a
                      different height on every card depending on how long the
                      category was. */}
                  <div className="flex items-start gap-2.5 min-w-0">
                    {hasBrandMark(resource.id) ? (
                      <BrandMark brandId={resource.id} size={28} />
                    ) : (
                      <span className="text-2xl leading-none">{resource.emoji}</span>
                    )}
                    <div>
                      <h3 className="font-['Fraunces'] text-base font-semibold text-foreground leading-tight">
                        {resource.name}
                      </h3>
                      <p className="text-[10px] text-muted-foreground font-semibold tracking-widest uppercase mt-0.5">
                        {resource.category}
                      </p>
                    </div>
                  </div>
                  <span className="ml-2 shrink-0">
                    <PricingBadge types={resource.pricingTypes} />
                  </span>
                </div>

                {/* Description */}
                <p className="text-sm text-foreground/75 leading-relaxed mb-3 flex-1">
                  {resource.description}
                </p>

                {/* Best For */}
                <p className="text-xs text-muted-foreground italic mb-3">
                  {resource.bestFor}
                </p>

                {/* Use Case Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {resource.useCases.map(useCase => (
                    <span
                      key={useCase}
                      className="text-xs bg-secondary text-foreground/70 px-2 py-0.5 rounded-full border border-border"
                    >
                      {useCase}
                    </span>
                  ))}
                </div>

                {/* Footer: pricing + skill level + buttons */}
                <div className="border-t border-border pt-3 mt-auto">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground truncate pr-2">{resource.pricing}</p>
                    <span className="text-xs text-muted-foreground font-medium shrink-0">{resource.skillLevel}</span>
                  </div>
                  {/* One button, in the row the tool cards use.
                      
                      The "Visit" text link beside it is gone, for the reason
                      the tool cards dropped theirs: every entry here has a
                      resource page, and that page's step 1 is the outbound
                      link. So the affiliate click is one step deeper than it
                      was rather than lost, `resource.website` is still in the
                      data, and the card stops offering two destinations of
                      very different weight side by side. */}
                  <div className="flex items-center justify-between gap-2 mt-2.5">
                    {resource.routeLink && (
                      <Link href={resource.routeLink} className={BUTTON.primarySm}>
                        See how to start
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The back link that used to sit here, at the foot of a long grid of
          cards, is in the sticky header now. Somewhere you have to scroll to
          reach is the wrong home for "take me back". */}

      {/* The one page with the site header that had no site footer, so it was
          the only route where scrolling to the bottom left you with nowhere to
          go but the back link above. */}
      <SiteFooter />
    </div>
  );
}
